import {
  ScreenerDataSourceType,
  CompanyMetric,
  TableCellType,
  CellType,
} from "../../models/tableModels";
import { ColumnType } from "antd/es/table";
import { ColumnType as CustomColumnType } from "../../models/tableModels";
import { MESSAGES } from "../../messages";
import { Filter as Option } from "../../types/PageFilterTypes";
import { SCREENER_COLUMN_TYPES } from "./constants";
import { buildFlagOptions } from "../../components/visuals/StatusFlag";
import { FundNamesItem } from "../../types/CommonDataRequestTypes";
import {
  CompassMetric,
  GICSSector,
  HoldingCompany,
  SelectableMetric,
} from "../../types/ScreenerTypes";
import { formatReadable } from "../../utils/formattingUtils";
import { renderNumberRangeFilter } from "../../components/tables/NumericFilter";
import { renderTextSearchFilter } from "../../components/tables/TextFilter";

/**
 * Generates a list of unique column options for display based on provided metrics data.
 * Filters to only include metrics whose IDs are in the provided `metrics` list.
 * Ensures uniqueness based on Compass_Metric_Id and builds column definitions.
 *
 * @param data Array of metrics, each containing details like metric ID and display name.
 * @param metrics List of metric IDs to include (from filters).
 * @returns An array of unique column definitions.
 */
export const getDynamicColumns = (
  data: CompassMetric[],
  metrics: string[] = []
): ColumnType<ScreenerDataSourceType>[] => {
  const metricMap = new Map(
    data.map((metric) => [metric.Compass_Metric_Id, metric])
  );

  const uniqueIds = new Set<string>();

  return metrics.reduce((acc: ColumnType<ScreenerDataSourceType>[], id) => {
    const metric = metricMap.get(id);
    if (metric && !uniqueIds.has(id)) {
      uniqueIds.add(id);
      acc.push({
        key: id,
        title: metric.Compass_Metric_Display_Name,
        dataIndex: id,
        sorter: true,
        filterDropdown: (props) =>
          metric.Is_Numeric
            ? renderNumberRangeFilter(props)
            : renderTextSearchFilter(props),
      });
    }
    return acc;
  }, []);
};

/**
 * Constructs and formats a structured data source suited for antd table datasource for funds from provided metrics.
 * It organizes data into indexed and non-indexed funds, assigns unique keys,
 * and interleaves indexes with portfolio data for structured presentation.
 *
 * @param data Array containing various metrics for different funds.
 * @returns A two-dimensional array with separate lists for interleaved funds and indexes.
 */
export const buildScreenerDataSource = (
  data: CompanyMetric[]
): ScreenerDataSourceType[] => {
  const screenerData: ScreenerDataSourceType[] = [];

  data.forEach((item, index) => {
    let existingItem = screenerData.find((res) => res.name === item.name);

    if (!existingItem) {
      existingItem = {
        portfolio_index_id: item.portfolio_index_id,
        sector: item.gics_sector_name,
        currency: item.currency,
        name: item.name,
        compass_cid: item.compass_cid,
        added_on: item.added_on,
        holding_weight: item.holding_weight,
        revenue: item.revenue,
        image: item.image,
        flagStatus: item.flag_status,
        flagsDropdown: {
          options: buildFlagOptions(item.flag_status),
          isInTable: true,
        },
        key: `${item.portfolio_index_id}-${item.name}-${index}`,
        companyAndLogo: {
          name: item.name,
          logoUrl: item.image,
          companyId: item.compass_cid,
        },
      };
      screenerData.push(existingItem);
    }

    existingItem[item.metric_id] =
      item.value_numeric ||
      (item.value_text !== "0" ? item.value_text : "") ||
      "";
  });

  return screenerData;
};

/**
 * Returns the column type based on the provided column key and value.
 * @param column - The column key.
 * @param value - The value to determine the column type.
 * @param loading - Optional. Indicates if the table cell is in a loading state. Default is false.
 * @returns The column type object.
 */
export const getColumnTypeFromKeyOrValue = (
  column: string,
  value: CellType,
  loading = false
): CustomColumnType => {
  if (loading) return { columnKey: column, type: TableCellType.LOADING };
  if (value === MESSAGES.NOT_AVAILABLE)
    return { columnKey: column, type: TableCellType.TEXT };
  for (const columnType of SCREENER_COLUMN_TYPES) {
    if (columnType.columnKey === column) {
      return columnType;
    }
  }

  // Fallback based on the type of value
  if (typeof value === "number") {
    return { columnKey: column, type: TableCellType.NUMBER };
  }
  return { columnKey: column, type: TableCellType.TEXT };
};

export const buildFundsOptions = (data: FundNamesItem[]): Option[] => {
  return data
    .filter((item) => !item.is_index)
    .map((item) => ({
      displayValue: item.portfolio_index_id,
      key: item.portfolio_index_id,
      selectorName: "portfolio_index_id",
    }))
    .sort((a, b) => a.displayValue.localeCompare(b.displayValue));
};

export const buildSectorOptions = (data: GICSSector[]): Option[] => {
  return data
    .map((item) => ({
      displayValue: item.gics_sector_name,
      key: item.gics_sector_code as unknown as string,
      selectorName: "gics_sector_code",
    }))
    .sort((a, b) => a.displayValue.localeCompare(b.displayValue));
};

export const buildMetricsOptions = (
  data: CompassMetric[]
): SelectableMetric[] => {
  return data
    .map((item) => ({
      displayValue: item.Compass_Metric_Display_Name,
      key: item.Compass_Metric_Id,
      selectorName: "metric_id",
      description: item.Compass_Metric_Description,
      type: item.Metric_Type,
      level_1: item.Level_1,
      level_2: item.Level_2,
      level_3: item.Level_3,
    }))
    .sort((a, b) => a.displayValue.localeCompare(b.displayValue));
};

export const buildCompaniesOptions = (data: HoldingCompany[]): Option[] => {
  return data.map((item) => ({
    displayValue: formatReadable(item.name),
    key: item.name,
    selectorName: "name",
  }));
};
